import React, { useCallback, useState } from 'react';
import { Banner, Popover } from '@round/ui-kit';
import Card from '../../../../ui/Card/Card';
import styles from './AddClientSongToCollection.module.css';
import { useClientSongCollectionOptions } from './ClientSongCollectionsContext';
import OptionsList from '../../../../ui/DataEntry/SelectBox/components/OptionsList/OptionsList';
import CheckboxOption from '../../../../ui/DataEntry/SelectBox/components/CheckboxOption/CheckboxOption';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import { showNotification } from '../../../../helpers';
import {
    addClientSongToSongCollection,
    ApiResponse,
    ClientSongCollection,
    getClientSongCollection,
    removeClientSongFromSongCollection,
} from '@round/api';
import { SelectBoxOptionWithId } from '../../../../ui/DataEntry/SelectBox/SelectBox.types.';
import uniqBy from 'lodash/uniqBy';

type AddClientSongToCollectionProps = {
    clientId: number;
    songId: number;
    collectionsId: number[];
    renderAnchor: (props: {
        selectedCollectionsCount: number;
        setRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
    }) => React.ReactNode;
};

const AddClientSongToCollection = ({
    renderAnchor,
    collectionsId,
    songId,
    clientId,
}: AddClientSongToCollectionProps) => {
    const [popoverAnchorRef, setPopoverAnchorRef] = useState<HTMLElement | null>(null);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [orderingPriorityCollections, setOrderingPriorityCollections] = useState<number[]>(collectionsId);

    const { collections, loading, initiated, initiate, count, search, loadMore, hasErrorLoading } =
        useClientSongCollectionOptions(clientId);

    const {
        selectedCollections,
        initCollections: initSelectedCollections,
        loading: selectedCollectionsLoading,
        isSelected,
        addSongToCollection,
        removeSongFromCollection,
        isInitiated,
        hasErrorInitiating,
    } = useManageSongsCollections(clientId, songId, collectionsId);

    const onShowPopoverChange = useCallback(
        async (show: boolean) => {
            setIsDropdownOpen(show);
            if (!show && isInitiated) {
                setOrderingPriorityCollections(selectedCollections.map((c) => c.id));
                return;
            }

            if (!isInitiated) {
                await initSelectedCollections();
            }
        },
        [initSelectedCollections, isInitiated, selectedCollections]
    );

    useAbortableEffect(
        (signal) => {
            if ((searchInputValue !== search || !initiated) && isDropdownOpen) {
                initiate(searchInputValue, { signal });
            }
        },
        [searchInputValue, search, initiate, isDropdownOpen, initiated]
    );

    const mapCollectionToOption = (collection: ClientSongCollection): SelectBoxOptionWithId => ({
        id: collection.id,
        picture: collection.picture ?? '',
        name: collection.name,
    });

    const options = uniqBy(
        collections.map(mapCollectionToOption).concat(selectedCollections.map(mapCollectionToOption)),
        'id'
    ).sort((a, b) => {
        if (searchInputValue) {
            return 0;
        }

        return Number(orderingPriorityCollections.includes(b.id)) - Number(orderingPriorityCollections.includes(a.id));
    });

    return (
        <>
            {renderAnchor({
                setRef: setPopoverAnchorRef,
                selectedCollectionsCount: isInitiated ? selectedCollections.length : collectionsId.length,
            })}
            <Popover
                anchorElement={popoverAnchorRef}
                showOn="click"
                options={{ placement: 'bottom-start' }}
                onShowPopoverChange={onShowPopoverChange}
            >
                <Card className={styles.dropdown}>
                    <OptionsList
                        searchable
                        searchQuery={searchInputValue}
                        setSearchQuery={setSearchInputValue}
                        options={options}
                        count={count}
                        loading={loading || selectedCollectionsLoading}
                        loadMore={loadMore}
                    >
                        {(props) => {
                            return (
                                <CheckboxOption
                                    {...props}
                                    disabled={hasErrorInitiating}
                                    checked={isSelected(props.item?.id)}
                                    onClick={async (item) => {
                                        if (!item || hasErrorInitiating) {
                                            return;
                                        }

                                        const selectedCollection = selectedCollections.find((c) => c.id === item.id);
                                        if (selectedCollection) {
                                            await removeSongFromCollection(selectedCollection);
                                            return;
                                        }

                                        const collection = collections.find((c) => c.id === item.id);
                                        if (collection) {
                                            await addSongToCollection(collection);
                                            return;
                                        }
                                    }}
                                />
                            );
                        }}
                    </OptionsList>
                    {hasErrorInitiating && (
                        <Banner appearance="warning">
                            Error occurred during initialization of selected collections
                        </Banner>
                    )}

                    {hasErrorLoading && <Banner appearance="danger">Error occurred during loading collections</Banner>}
                </Card>
            </Popover>
        </>
    );
};

function useManageSongsCollections(clientId: number, songId: number, initialCollectionIds: number[]) {
    const [selectedCollections, setSelectedCollections] = useState<ClientSongCollection[]>([]);
    const [isInitiated, setIsInitiated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasErrorInitiating, setHasErrorInitiating] = useState(false);

    const initCollections = useCallback(async () => {
        try {
            setLoading(true);
            setHasErrorInitiating(false);
            const responses = await Promise.all(
                initialCollectionIds.map((id) => getClientSongCollection({ clientId, collectionId: id }))
            );

            const collections = responses
                .filter((response): response is ApiResponse<ClientSongCollection, 200> => response.status === 200)
                .map((response) => response.data);
            setSelectedCollections(collections);
            setIsInitiated(true);
        } catch {
            setHasErrorInitiating(true);
            showNotification('Could not fetch selected collections', 'error');
        } finally {
            setLoading(false);
        }
    }, [clientId, initialCollectionIds]);

    const isSelected = (collectionId: number | undefined) => {
        if (typeof collectionId !== 'number') {
            return false;
        }

        if (isInitiated) {
            return !!selectedCollections.find((c) => c.id === collectionId);
        }

        return initialCollectionIds.includes(collectionId);
    };

    const addSongToCollection = useCallback(
        async (collection: ClientSongCollection) => {
            try {
                const response = await addClientSongToSongCollection({
                    songId,
                    songCollectionId: collection.id,
                    clientId,
                });

                if (response.status === 204) {
                    setSelectedCollections((collections) => collections.concat(collection));
                    showNotification('Added song to collection', 'info');
                    return;
                }

                const errorMessage = response.status === 403 ? response.data.detail : response.data.join('. ');
                showNotification(errorMessage, 'error');
            } catch {
                showNotification('Could not add song to song collection', 'error');
            }
        },
        [clientId, songId]
    );

    const removeSongFromCollection = useCallback(
        async (collection: ClientSongCollection) => {
            try {
                const response = await removeClientSongFromSongCollection({
                    songCollectionId: collection.id,
                    songId,
                    clientId,
                });

                if (response.status === 204) {
                    setSelectedCollections((collections) => collections.filter((c) => c.id !== collection.id));
                    showNotification('Removed song from collection', 'info');
                    return;
                }

                const errorMessage = response.status === 403 ? response.data.detail : response.data.join('. ');
                showNotification(errorMessage, 'error');
            } catch {
                showNotification('Could not remove song from song collection', 'error');
            }
        },
        [clientId, songId]
    );

    return {
        isInitiated,
        initCollections,
        selectedCollections,
        loading,
        hasErrorInitiating,
        isSelected,
        addSongToCollection,
        removeSongFromCollection,
    };
}

export default AddClientSongToCollection;
